/**
 * Phase 2b — MDX components for the public docs renderer.
 * Each is a small, opinionated React component matching the design blueprint
 * at /app/design_guidelines.json.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Copy, Check, Info, AlertTriangle, AlertOctagon, CheckCircle2, Hash, Rss, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Callout — `<callout type="info|success|warning|critical">…</callout>`
// ---------------------------------------------------------------------------

const CALLOUT_VARIANTS = {
  info:     { color: "#00E5FF", icon: Info,         label: "INFO" },
  success:  { color: "#1FDD83", icon: CheckCircle2, label: "SUCCESS" },
  warning:  { color: "#FFB000", icon: AlertTriangle,label: "WARNING" },
  critical: { color: "#FF6E6E", icon: AlertOctagon, label: "CRITICAL" },
};

export function Callout({ type = "info", title, children }) {
  const v = CALLOUT_VARIANTS[type] || CALLOUT_VARIANTS.info;
  const Icon = v.icon;
  return (
    <div
      data-testid={`docs-callout-${type}`}
      className="my-6 p-4 rounded-r-sm"
      style={{
        borderLeft: `2px solid ${v.color}`,
        background: `linear-gradient(to right, ${v.color}0D, transparent 60%)`,
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} style={{ color: v.color }} />
        <span
          className="text-[10px] tracking-[0.18em] uppercase font-mono font-semibold"
          style={{ color: v.color }}
        >
          {v.label} //
        </span>
        {title && (
          <span className="text-sm font-semibold" style={{ color: v.color }}>
            {title}
          </span>
        )}
      </div>
      <div className="text-sm leading-relaxed" style={{ color: "#A8B7CC" }}>
        {children}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// CodeBlock — wraps the rendered <code> from rehype-highlight with a header
// strip, copy button, and dark code-bg matching the spec.
//
// Note: this component does NOT wrap in <pre>. The caller (the `pre` slot of
// the markdown renderer) provides the block context, so we use a <pre>-like
// styled <div> with role="region" to keep semantics while sidestepping the
// `<pre> inside <p>` hydration trap react-markdown can hit.
// ---------------------------------------------------------------------------

export function CodeBlock({ filename, language, children }) {
  const [copied, setCopied] = useState(false);
  const ref = React.useRef(null);

  const onCopy = async () => {
    try {
      const text = ref.current?.innerText || "";
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      data-testid="docs-codeblock"
      className="my-6 rounded-md overflow-hidden"
      style={{
        background: "#02040B",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <div className="font-mono text-[11px]" style={{ color: "#8B9BB4" }}>
          {filename || (language ? language.toUpperCase() : "CODE")}
        </div>
        <button
          type="button"
          onClick={onCopy}
          data-testid="docs-codeblock-copy"
          className="flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded hover:text-white transition-colors"
          style={{ color: copied ? "#00E5FF" : "rgba(255,255,255,0.5)" }}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div
        ref={ref}
        role="region"
        aria-label={language ? `${language} code block` : "code block"}
        className="qt-docs-codeblock-body p-4 text-[13px] leading-relaxed overflow-x-auto"
        style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
      >
        {children}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// APIBlock — `<apiblock method="GET" path="/api/foo">…</apiblock>`
// ---------------------------------------------------------------------------

const METHOD_COLORS = {
  GET:    { bg: "rgba(0,229,255,0.18)",   fg: "#00E5FF" },
  POST:   { bg: "rgba(123,217,168,0.18)", fg: "#1FDD83" },
  PUT:    { bg: "rgba(255,176,0,0.18)",   fg: "#FFB000" },
  PATCH:  { bg: "rgba(255,176,0,0.18)",   fg: "#FFB000" },
  DELETE: { bg: "rgba(255,110,110,0.18)", fg: "#FF6E6E" },
};

export function APIBlock({ method = "GET", path, children }) {
  const m = (method || "GET").toUpperCase();
  const c = METHOD_COLORS[m] || METHOD_COLORS.GET;
  return (
    <div
      data-testid="docs-apiblock"
      className="my-6 rounded-md overflow-hidden"
      style={{
        background: "#0A1224",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div
        className="flex items-center gap-3 p-3"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <span
          className="px-2 py-0.5 text-[11px] font-mono font-bold tracking-wider"
          style={{ background: c.bg, color: c.fg, borderRadius: 2 }}
        >
          {m}
        </span>
        <span className="font-mono text-sm" style={{ color: "#F8FAFC" }}>
          {path}
        </span>
      </div>
      <div className="p-4 text-sm" style={{ color: "#8B9BB4" }}>
        {children}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// VideoBlock — minimum-viable HTML5 wrapper. Custom chrome is Phase 8 polish.
// ---------------------------------------------------------------------------

export function VideoBlock({ src, poster, title }) {
  if (!src) return null;
  return (
    <figure
      data-testid="docs-video"
      className="my-8 rounded-md overflow-hidden"
      style={{ border: "1px solid rgba(255,255,255,0.06)" }}
    >
      <video
        src={src}
        poster={poster}
        controls
        preload="metadata"
        className="w-full h-auto block"
        style={{ background: "#02040B" }}
      />
      {title && (
        <figcaption
          className="px-4 py-2 text-xs font-mono"
          style={{
            color: "#8B9BB4",
            background: "rgba(255,255,255,0.02)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {title}
        </figcaption>
      )}
    </figure>
  );
}


// ---------------------------------------------------------------------------
// Diagram — lazy-loads Mermaid only when an actual diagram is rendered, so
// the ~600KB mermaid bundle stays out of the docs landing & article hot path.
// Falls back to a styled source-card if mermaid fails to load (no flashes).
// ---------------------------------------------------------------------------

function _extractText(node) {
  if (node == null) return "";
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(_extractText).join("");
  if (typeof node === "object" && node.props) return _extractText(node.props.children);
  return String(node);
}

let _mermaidPromise = null;
function _loadMermaid() {
  if (_mermaidPromise) return _mermaidPromise;
  _mermaidPromise = import(/* webpackChunkName: "mermaid" */ "mermaid")
    .then((mod) => {
      const m = mod.default || mod;
      m.initialize({
        startOnLoad: false,
        theme: "dark",
        themeVariables: {
          background: "#0A1224",
          primaryColor: "#0E172E",
          primaryTextColor: "#F8FAFC",
          primaryBorderColor: "#00E5FF",
          lineColor: "#8B9BB4",
          tertiaryColor: "#02040B",
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
        },
        securityLevel: "strict",
      });
      return m;
    });
  return _mermaidPromise;
}

export function Diagram({ children }) {
  const source = _extractText(children).trim();
  const [svg, setSvg] = useState("");
  const [error, setError] = useState(false);
  const idRef = useRef(`mmd-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    if (!source) return undefined;
    let cancelled = false;
    _loadMermaid()
      .then((mermaid) => mermaid.render(idRef.current, source))
      .then((res) => {
        if (cancelled) return;
        const out = (res && (res.svg || res)) || "";
        setSvg(out);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => { cancelled = true; };
  }, [source]);

  if (error || !source) {
    // Fallback — keeps the original "interactive diagram" placeholder card.
    return (
      <div
        data-testid="docs-diagram"
        className="my-8 p-4 rounded-md font-mono text-xs"
        style={{
          background: "#0A1224",
          border: "1px dashed rgba(0,229,255,0.20)",
          color: "#8B9BB4",
        }}
      >
        <div
          className="text-[10px] tracking-[0.18em] uppercase mb-2"
          style={{ color: "var(--qt-cyan)" }}
        >
          Diagram //
        </div>
        <pre className="whitespace-pre-wrap" style={{ margin: 0 }}>
          {source || children}
        </pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div
        data-testid="docs-diagram-loading"
        className="my-8 p-6 rounded-md flex items-center justify-center"
        style={{ background: "#0A1224", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <Loader2 size={16} className="animate-spin" style={{ color: "var(--qt-cyan)" }} />
      </div>
    );
  }

  return (
    <div
      data-testid="docs-diagram"
      className="my-8 p-4 rounded-md flex items-center justify-center"
      style={{
        background: "#0A1224",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      // SVG is generated server-side by mermaid; safe to inject because
      // securityLevel:strict sandboxes user-supplied diagram source.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}


// ---------------------------------------------------------------------------
// LatestRelease — `<latestrelease count="1" />` dynamic block embedded in
// any markdown doc. Pulls /api/docs/changelog/latest and renders the same
// release card aesthetic the email block uses.
// ---------------------------------------------------------------------------

export function LatestRelease({ count = 1 }) {
  const n = Math.max(1, Math.min(parseInt(count, 10) || 1, 5));
  const [items, setItems] = useState([]);
  const [pageUrl, setPageUrl] = useState("/docs/changelog");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios.get(`${API}/api/docs/changelog/latest`, { params: { count: n } })
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
        if (r.data?.page_url) setPageUrl(r.data.page_url);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [n]);

  if (loading) {
    return (
      <div
        data-testid="docs-latest-release-loading"
        className="my-6 p-4 flex items-center gap-2 rounded-r-sm"
        style={{
          borderLeft: "3px solid #00E5FF",
          background: "rgba(0,229,255,0.05)",
          color: "#8B9BB4",
        }}
      >
        <Loader2 size={14} className="animate-spin" style={{ color: "var(--qt-cyan)" }} />
        <span className="font-mono text-xs">Loading latest release…</span>
      </div>
    );
  }

  if (error || items.length === 0) {
    return null;
  }

  return (
    <div data-testid="docs-latest-release">
      {items.map((it, i) => (
        <div
          key={`${it.date}-${i}`}
          className="my-6 p-4 rounded-r-sm"
          style={{
            borderLeft: "3px solid #00E5FF",
            background: "rgba(0,229,255,0.05)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Rss size={12} style={{ color: "#FFB000" }} />
            <span
              className="text-[10px] tracking-[0.18em] uppercase font-mono font-semibold"
              style={{ color: "#FFB000" }}
            >
              RELEASE {it.date ? `· ${it.date}` : ""}
            </span>
          </div>
          <div
            className="font-heading font-semibold text-lg mb-2"
            style={{ color: "#FFFFFF" }}
          >
            {it.title}
          </div>
          {it.summary && (
            <div
              className="text-sm leading-relaxed mb-2"
              style={{ color: "#A8B7CC" }}
            >
              {it.summary}
            </div>
          )}
          <a
            href={it.link || pageUrl}
            className="text-sm font-medium qt-docs-link"
            style={{ color: "var(--qt-cyan)" }}
          >
            Read more →
          </a>
        </div>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// HoverAnchor — drops a `#` symbol in the left margin of an h2/h3 on hover.
// ---------------------------------------------------------------------------

export function HoverAnchor({ id }) {
  if (!id) return null;
  return (
    <a
      href={`#${id}`}
      aria-label={`Permalink to section ${id}`}
      className="qt-docs-anchor"
      onClick={(e) => {
        // Smooth-scroll AND copy URL to clipboard for shareability.
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          history.replaceState(null, "", `#${id}`);
          try {
            navigator.clipboard.writeText(window.location.href);
          } catch {
            /* ignore */
          }
        }
      }}
    >
      <Hash size={14} />
    </a>
  );
}
